import { useMemo, useState } from "react";
import { Timer } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { weeksOf, dayLabel, fmtClock, fmtDur } from "../lib/pickerSheet";
import { slug } from "../lib/format";

const ALL = "all";

export const OvertimeView = ({ users, cfg, onOpen }) => {
  const weeks = useMemo(() => weeksOf(users), [users]);
  const [week, setWeek] = useState(weeks[weeks.length - 1]);
  const rows = useMemo(() => users.map((u) => {
    const days = (week === ALL ? u.days : u.weeks.find((w) => w.week === week)?.days || []);
    const ot = days.filter((d) => d.ot > 0);
    return { id: u.id, shift: u.mainShift, worked: days.length, otDays: ot.length, ot: ot.reduce((s, d) => s + d.ot, 0), details: ot };
  }).filter((r) => r.otDays).sort((a, b) => b.ot - a.ot), [users, week]);
  const max = Math.max(1, ...rows.map((r) => r.ot));
  return (
    <div className="panel" data-testid="overtime-view">
      <div className="flex flex-wrap items-center gap-3 p-5 border-b border-slate-800">
        <Timer className="h-5 w-5 text-cyan-400" />
        <div className="mr-auto">
          <h3 className="font-display text-lg font-semibold text-slate-100">Overtime flags</h3>
          <p className="text-xs text-slate-500" data-testid="overtime-summary">Days started before or finished after the picker's shift by {cfg.otMin ?? 60}+ min · {rows.length} pickers flagged</p>
        </div>
        <Select value={week} onValueChange={setWeek}>
          <SelectTrigger data-testid="overtime-week-select" className="h-9 w-[180px] bg-[#0B0F19] border-slate-700 text-slate-100 font-mono text-xs"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-80">
            <SelectItem value={ALL} className="font-mono text-xs">Whole date range</SelectItem>
            {[...weeks].reverse().map((w) => <SelectItem key={w} value={w} className="font-mono text-xs">w/c {dayLabel(w)} {w.slice(0, 4)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-800">{["Picker", "Shift", "OT days", "OT time", "Days", ""].map((h, i) => <th key={i} className="text-left px-3 py-3 font-mono text-[11px] uppercase tracking-wider text-slate-500 whitespace-nowrap first:pl-5">{h}</th>)}</tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="px-5 py-6 text-sm text-slate-500">No overtime in this period.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`overtime-row-${slug(r.id)}`} onClick={() => onOpen(r.id)} className="border-b border-slate-800/60 hover:bg-slate-800/40 cursor-pointer align-top">
                <td className="pl-5 py-2.5 text-slate-100 font-medium whitespace-nowrap">{r.id}<div className="h-1 mt-1.5 rounded bg-slate-800 w-28"><div className="h-1 rounded bg-cyan-500" style={{ width: `${(r.ot / max) * 100}%` }} /></div></td>
                <td className="px-3 py-2.5 text-xs text-slate-400 whitespace-nowrap">{r.shift}</td>
                <td className="px-3 py-2.5 font-mono text-cyan-300">{r.otDays} / {r.worked}</td>
                <td className="px-3 py-2.5 font-mono text-cyan-300 font-medium whitespace-nowrap">{fmtDur(r.ot)}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">{r.details.slice(-12).map((d) => (
                    <span key={d.day} className="font-mono text-[10px] px-1.5 py-0.5 rounded border border-cyan-500/30 bg-cyan-500/10 text-cyan-300 whitespace-nowrap" title={`Shift ${fmtClock((d.shiftStart + 86400) % 86400)}–${fmtClock(d.shiftEnd % 86400)}`}>
                      {dayLabel(d.day)} {fmtClock(d.first)}–{fmtClock(d.last)} · +{fmtDur(d.ot)}
                    </span>
                  ))}{r.details.length > 12 && <span className="text-[10px] text-slate-500">+{r.details.length - 12} more</span>}</div>
                </td>
                <td />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
